import React, { FC } from "react";
import SearchIcon from "@mui/icons-material/Search";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Checkbox,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Typography,
  InputBase,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import getOptions from "./search.biz";

const BootstrapInput = styled(InputBase)(({ theme }) => ({
  "label + &": {
    marginTop: theme.spacing(1),
  },
  borderRadius: 4,
  "& .MuiInputBase-input": {
    borderRadius: 4,
    position: "relative",
    backgroundColor: "lightgrey",
    border: "1px solid #ced4da",
    fontSize: 16,
    padding: "10px 26px 10px 12px",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    fontFamily: ["Arial"].join(","),
    "&:focus": {
      borderRadius: 4,
      borderColor: "#80bdff",
      boxShadow: "0 0 0 0.2rem rgba(0,123,255,.25)",
    },
  },
}));

const StyledSearchDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    width: "100%",
  },
}));

const StyledSearchWrapDiv = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginTop: 20,
  paddingBottom: 20,
  backgroundColor: "#fafafa",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    width: "100%",
  },
}));

const StyledResetButton = styled("button")({
  "&:hover": {
    cursor: "pointer",
  },
  marginLeft: 10,
  borderRadius: 5,
});

const StyledButton = styled(Button)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginLeft: 20,
  color: "black",
});

const StyledDivRow = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: 10,
    justifyContent: "space-around",
  },
}));

const StyledRowDivCenter = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: 10,
});

const StyledFormControl = styled(FormControl)({
  paddingRight: 10,
  width: 200,
  borderRadius: 10,
});

interface SearchProps {
  filterBy: string;
  filterSearch?: () => void;
  handleFilterByChange: (e: SelectChangeEvent<string>) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  handleReset?: () => void;
  showTab: string;
  searchTerm: string;
  handleSearchBtn?: () => void;
  showArchive?: string;
  handleArchive?: (e: SelectChangeEvent<string>) => void;
  showActive?: string;
  handleActive?: (e: SelectChangeEvent<string>) => void;
  showLeadsOnly?: boolean;
  handleShowLeadsOnly?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const SearchBar: FC<SearchProps> = ({
  filterBy,
  filterSearch,
  handleFilterByChange,
  handleChange,
  onKeyDown,
  handleReset,
  showTab,
  searchTerm,
  handleSearchBtn,
  showArchive,
  handleArchive,
  showActive,
  handleActive,
  showLeadsOnly,
  handleShowLeadsOnly,
}) => {
  return (
    <StyledSearchWrapDiv>
      <StyledSearchDiv>
        <StyledDivRow>
          <StyledFormControl
            variant="outlined"
            margin="dense"
            disabled={showTab === "Calendar"}
          >
            <InputLabel>Filter Search</InputLabel>
            <Select
              style={{ backgroundColor: "lightgrey" }}
              native
              value={filterBy}
              onChange={handleFilterByChange}
              label="Filter Search"
              input={<BootstrapInput />}
            >
              {getOptions(showTab)}
            </Select>
          </StyledFormControl>

          <TextField
            id="combo-box-demo"
            onChange={handleChange}
            onKeyPress={onKeyDown}
            size="small"
            style={{ width: 250, marginTop: 10 }}
            value={searchTerm}
            label="Search by Keyword..."
            variant="outlined"
          />
        </StyledDivRow>

        <StyledDivRow style={{ marginTop: 10 }}>
          <StyledButton
            onClick={() =>
              showTab === "Service Repair" && handleSearchBtn
                ? handleSearchBtn()
                : filterSearch
                  ? filterSearch()
                  : null
            }
            size="large"
            variant="contained"
            color={showTab === "Glass Details" ? "customGrey" : "cyan"}
            style={{ color: showTab === "Glass Details" ? "white" : "black" }}
          >
            <SearchIcon />
            <Typography style={{ fontWeight: "bold" }} variant="body2">
              Search
            </Typography>
          </StyledButton>
          {showLeadsOnly !== undefined && <FormControlLabel
            style={{ marginLeft: 10 }}
            control={
              <Checkbox
                checked={!!showLeadsOnly}
                onChange={handleShowLeadsOnly}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
              />
            }
            label="Show Leads Only"
          />}
          {showTab !== "Calendar" && showTab !== "Glass Details" && (
            <StyledResetButton onClick={handleReset}>Reset</StyledResetButton>
          )}
        </StyledDivRow>
      </StyledSearchDiv>

      {showTab === "Insights" && (
        <StyledRowDivCenter>
          <Typography style={{ paddingRight: 10 }}>Show:</Typography>

          <Select size="small" value={showArchive} onChange={handleArchive}>
            <MenuItem value="All">All</MenuItem>
            <MenuItem value="Quotes">Quotes</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
            <MenuItem value="Quotes/Archived">Quotes/Archived</MenuItem>
            <MenuItem value="Orders">Orders</MenuItem>
          </Select>
        </StyledRowDivCenter>
      )}

      {showTab === "Quotes" && (
        <StyledRowDivCenter>
          <Typography style={{ paddingRight: 10 }}>Show:</Typography>

          <Select size="small" value={showArchive} onChange={handleArchive}>
            <MenuItem value="Quotes">Quotes</MenuItem>
            <MenuItem value="Archived">Archived</MenuItem>
            <MenuItem value="Quotes/Archived">Quotes/Archived</MenuItem>
          </Select>
        </StyledRowDivCenter>
      )}

      {showTab === "Customer List" && (
        <StyledRowDivCenter>
          <Typography style={{ paddingRight: 10 }}>Show:</Typography>

          <Select size="small" value={showActive} onChange={handleActive}>
            <MenuItem value="active">Active Accounts</MenuItem>
            <MenuItem value="inactive">Inactive Accounts</MenuItem>
          </Select>
        </StyledRowDivCenter>
      )}
    </StyledSearchWrapDiv>
  );
};

export default SearchBar;
